import { useMemo } from 'react';

import {
  type UseIntegrationHealthOptions,
  useIntegrationHealth,
} from '@/hooks/useIntegrationHealth';
import type { ConnectedAccount } from '@/hooks/usePlatformIntegrations';
import {
  type ConnectionAttention,
  attentionLabel,
  deriveConnectionAttention,
} from '@/lib/platforms/connection-attention';
import { deriveConnectionStatus } from '@/lib/platforms/connection-status';
import { getIntegrationDetailSpec } from '@/lib/platforms/integration-detail-spec';
import { shopifyMissingRequiredScopes } from '@/lib/platforms/shopify-permissions';

export interface ConnectionAttentionState extends ConnectionAttention {
  label: 'Not working' | 'Needs attention' | null;
  isPending: boolean;
}

export function connectionHealthInputs(
  platform: string,
  account: ConnectedAccount,
): { hasScheduledSync: boolean; options: UseIntegrationHealthOptions } {
  const spec = getIntegrationDetailSpec(platform);
  const hasScheduledSync = spec.syncMode === 'scheduled';
  const selectedIds = (account.selectedResources ?? [])
    .map((resource) => resource.providerResourceId)
    .filter(Boolean);
  const healthAccountIds =
    selectedIds.length > 0 ? selectedIds : account.identifier ? [account.identifier] : [];
  return {
    hasScheduledSync,
    options: {
      syncs: hasScheduledSync,
      publishes: spec.publishes,
      dailyCoverage: hasScheduledSync && spec.dailyCoverage && platform !== 'shopify',
      connectionId: account.accountId,
      platformAccountIds: healthAccountIds,
    },
  };
}

export function useConnectionAttention(
  platform: string,
  label: string,
  account: ConnectedAccount,
): ConnectionAttentionState {
  const { hasScheduledSync, options } = useMemo(
    () => connectionHealthInputs(platform, account),
    [platform, account],
  );

  const { data: health, isPending } = useIntegrationHealth(platform, options);

  return useMemo(() => {
    const attention = deriveConnectionAttention({
      platform,
      label,
      account,
      tokenStatus: deriveConnectionStatus(account),
      health,
      hasScheduledSync,
      shopifyMissingScopes: platform === 'shopify' ? shopifyMissingRequiredScopes(account) : [],
    });
    return { ...attention, label: attentionLabel(attention.issues), isPending };
  }, [platform, label, account, health, hasScheduledSync, isPending]);
}
